/**
 * TaxCalendar — sidebar panel showing upcoming compliance dates.
 *
 * Desktop (XL+): sticky sidebar, scrollable list
 * Mobile:        collapsible accordion at bottom of page
 */
import * as React from 'react';
import { Calendar, ChevronDown, Bell } from 'lucide-react';
import { Skeleton } from '@finmin/ui';
import { useTaxCalendar } from '@/api/services';

const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  /** On XL, the sidebar is sticky. On mobile it's an accordion. */
  variant?: 'sidebar' | 'accordion';
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TaxCalendar({ variant = 'sidebar' }: Props) {
  const { data, isLoading } = useTaxCalendar();
  const events = data?.events ?? [];
  const customerType = data?.customer_type ?? '';

  const [open, setOpen] = React.useState(false);

  const subtitle =
    customerType === 'business_owner'
      ? 'Key dates for your business operations'
      : 'Key compliance dates for your finances';

  if (variant === 'accordion') {
    return (
      <div className="rounded-2xl border border-border bg-background overflow-hidden">
        {/* Accordion trigger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-4 text-left"
          aria-expanded={open}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: MINT }}
            >
              <Calendar className="h-4 w-4" style={{ color: FOREST }} aria-hidden />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Tax Calendar</p>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>

        {open && (
          <div className="border-t border-border px-4 pb-4 pt-2 animate-slide-down-fade origin-top">
            <CalendarList events={events} isLoading={isLoading} customerType={customerType} />
          </div>
        )}
      </div>
    );
  }

  // Sidebar variant
  return (
    <div className="rounded-2xl border border-border bg-background flex flex-col overflow-hidden sticky top-24 max-h-[calc(100vh-7rem)] animate-fade-in">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2.5">
          <div
            className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: MINT }}
          >
            <Calendar className="h-4 w-4" style={{ color: FOREST }} aria-hidden />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Tax Calendar</h3>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
      </div>

      {/* Scrollable event list */}
      <div className="overflow-y-auto flex-1 px-4 pb-4 pt-2">
        <CalendarList events={events} isLoading={isLoading} customerType={customerType} />
      </div>
    </div>
  );
}

// ── Shared event list ─────────────────────────────────────────────────────────

interface CalendarListProps {
  events: Array<{
    date_or_window: string;
    event_name: string;
    event_description: string;
    active_reminders: boolean;
  }>;
  isLoading: boolean;
  /** Used to tailor the empty state — backend returns no events for `others`. */
  customerType?: string;
}

function CalendarList({ events, isLoading, customerType }: CalendarListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3 pt-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-5 w-16 rounded shrink-0" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3 w-4/5" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return <CalendarEmptyState customerType={customerType} />;
  }

  return (
    <div className="divide-y divide-border">
      {/* Column headers */}
      <div className="grid grid-cols-[80px_1fr] gap-3 pb-2 pt-1">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          When
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          Event
        </span>
      </div>

      {events.map((evt, idx) => (
        <div
          key={`${evt.date_or_window}-${idx}`}
          className={`grid grid-cols-[80px_1fr] gap-3 py-2.5 items-start group animate-slide-up-fade stagger-${Math.min((idx % 8) + 1, 8)}`}
        >
          {/* Date chip */}
          <div className="shrink-0 pt-0.5">
            {evt.active_reminders ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md whitespace-nowrap"
                style={{
                  backgroundColor: `${FOREST}12`,
                  color: FOREST,
                  border: `1px solid ${FOREST}25`,
                }}
              >
                <Bell className="h-2.5 w-2.5" aria-hidden />
                {evt.date_or_window}
              </span>
            ) : (
              <span
                className="text-[10px] font-semibold whitespace-nowrap"
                style={{ color: FOREST }}
              >
                {evt.date_or_window}
              </span>
            )}
          </div>

          {/* Event info */}
          <div>
            <p className="text-xs font-medium text-foreground leading-snug">
              {evt.event_name}
            </p>
            {evt.event_description && (
              <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed line-clamp-2 group-hover:line-clamp-none transition-all">
                {evt.event_description}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Empty state ─────────────────────────────────────────────────────────────

interface CalendarEmptyStateProps {
  customerType?: string;
}

function CalendarEmptyState({ customerType }: CalendarEmptyStateProps) {
  // Backend returns no events for `customer_type = 'others'` by design.
  // Show a friendly explainer instead of the generic "no upcoming events".
  if (customerType === 'others') {
    return (
      <div className="py-8 px-2 text-center space-y-1.5">
        <p className="text-xs font-medium text-foreground">
          Tax calendar isn't available for your profile type yet
        </p>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          We'll let you know once we add deadlines for your category.
        </p>
      </div>
    );
  }

  return (
    <p className="py-8 text-center text-xs text-muted-foreground italic">
      No upcoming events
    </p>
  );
}
